import { quitarMarcasDeAcronimos } from '../lib/acronimos';
import { useNavigate } from 'react-router-dom';
import { rutaParaResponder } from '../lib/emailThreads';
import { useEffect, useRef, useState } from 'react';
import { avisarFalloDeCarga } from '../lib/fallos';
import { localeUI } from '../lib/format';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { uploadFile, imagesFromClipboard } from '../lib/upload';
import { KudosButton } from './KudosButton';
import { sanitizeRichHtml as sanitize } from '../lib/htmlSafe';
import { Avatar } from './Avatar';
import { MentionTextarea } from './MentionTextarea';
import { RichEditor, type RichEditorHandle } from './RichEditor';
import { EmailCard } from './email/EmailThread';
import { RecordingCard } from './rec/RecordingCard';
import { useAuth, hasRole } from '../lib/auth';
import {
  ClipboardList,
  AlarmClock, ArrowDown, CalendarDays, Check, ChevronDown, ChevronUp, Eye, FileArchive, FileSpreadsheet, FileText,
  HardHat, Headphones, Image, Mail, Monitor, Paperclip, Pencil, Puzzle, Search, Settings,
  Languages, Smartphone, Tablet, ThumbsUp, Trash2, X,
} from 'lucide-react';

type Att = { key: string; name: string };
type Msg = {
  id: string; department: string | null; author: string | null; author_user_id: string | null; body: string;
  attachment_key: string | null; attachment_name: string | null; attachments?: Att[]; source: string; created_at: string;
  parent_id: string | null; like_count: number; liked_by_me: boolean; liked_by?: string[]; is_follow_up?: boolean; co_forwarded_at?: string | null;
  /** Traducción guardada (EN ↔ ES), si alguien ya la pidió. */
  translations_json?: Record<string, { source_lang: string; target_lang: 'en' | 'es'; html: string }> | null;
  // Canal contractor: si ESTE mensaje sigue sin responderse y hace cuánto. Reemplaza a la alerta
  // urgente suelta que antes se acumulaba en la tarjeta del claim (pedido 2026-08-03).
  co_message_id?: string | null; co_unanswered?: boolean; co_waiting_hours?: number | null;
  email_id?: string | null; view_count?: number;
  /** Record (spec §4.12.5): FK `claim_messages.recording_id` → RecordingCard lazy por id. */
  recording_id?: number | string | null; recordingId?: number | string | null;
};
type Dept = { key: string; name: string; manual_follow_up_date?: boolean };

/**
 * Chat del claim por canales. `channel` = la key del depto (tab de depto) o null
 * (Overview = General). En un depto hay un toggle "All" para ver el general sin
 * salir. Postear en un depto también se ve en el General y se espeja a Monday.
 */

/**
 * COLOR POR ORIGEN DEL UPDATE.
 *
 * Pedido del manager después de ver la pestaña Historia: "me gusta cómo se ve, ¿pueden verse así
 * los updates? colores más oscuros para que resalten, y el nombre de quien lo dejó en negrita".
 *
 * Es el mismo lenguaje visual de `ClaimHistory`: fondo propio + barra de color a la izquierda. La
 * barra hace el trabajo — permite saltar por la lista y ubicar de dónde viene cada cosa sin leer
 * una palabra. Los colores son oscuros a propósito: los pasteles se pierden sobre el fondo claro,
 * que es justo lo que hacía que la lista se viera plana.
 */
const COLOR_UPDATE = (source: string | null, dept: string | null): string => {
  if (source === 'monday') return '#4338ca';   // vino del CRM
  if (source === 'partner') return '#b45309';  // lo escribió el contratista
  switch (dept) {
    case 'payments': return '#166534';
    case 'claims': return '#1d4ed8';
    case 'supplements': return '#b45309';
    case 'scheduling': return '#be185d';
    case 'intake': return '#7c3aed';
    case 'contractor': return '#b45309';
    default: return '#0d9488';
  }
};

/**
 * TASK DESDE LA NOTA (pedido del dueño 2026-09-12): un botón debajo de cada update para asignar
 * una tarea SOBRE esa nota, a una persona, un equipo o un departamento. Le llega al To Do (y como
 * DM y notificación) con el link al mensaje exacto (sourceMessageId), así se lee la nota en su
 * hilo. El título se prellena con el texto de la nota y se puede corregir.
 */
type Asignable = { id: string; name: string };
function TaskDesdeNota({ claimId, messageId, department, plain }: { claimId: string; messageId: string; department: string | null; plain: string }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<Asignable[]>([]);
  const [teams, setTeams] = useState<Asignable[]>([]);
  const [departments, setDepartments] = useState<Asignable[]>([]);
  const [assignee, setAssignee] = useState('');
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [busy, setBusy] = useState(false);
  const [hecho, setHecho] = useState(false);
  useEffect(() => {
    if (!open || users.length || teams.length) return;
    api.get<{ users: Asignable[]; teams: Asignable[]; departments?: Asignable[] }>('/api/team/mention-targets')
      // A propósito sin aviso: es el catálogo del autocompletado de @menciones. Si falla, el chat
      // funciona igual y solo no sugiere nombres — avisar acá sería gritar por un adorno.
      .then((r) => { setUsers(r.users || []); setTeams(r.teams || []); setDepartments(r.departments || []); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  function abrir() {
    setTitle(plain.replace(/\s+/g, ' ').trim().slice(0, 120));
    setOpen(true);
  }
  async function asignar() {
    if (!assignee || !title.trim()) return;
    const [kind, id] = assignee.split(':');
    setBusy(true);
    try {
      await api.post('/api/tasks', {
        claimId, title: title.trim(), note: plain.trim() || null, dueDate: due || null,
        assignedToUserId: kind === 'user' ? id : null, assignedToTeamId: kind === 'team' ? id : null, assignedToDepartmentId: kind === 'dept' ? id : null,
        sourceMessageId: messageId, sourceDepartment: department,
      });
      setHecho(true); setOpen(false); setAssignee(''); setDue('');
      window.setTimeout(() => setHecho(false), 2500);
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  if (hecho) return <span style={{ color: 'var(--green)', fontSize: 'var(--fs-2xs)', fontWeight: 600 }}>{t('task.created')}</span>;
  if (!open) {
    return (
      <button className="btn ghost sm" style={{ padding: '2px 6px', fontSize: 'var(--fs-2xs)' }} title={t('chat.taskFromNoteHint')} onClick={abrir}>
        <ClipboardList size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('chat.taskFromNote')}
      </button>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', flexBasis: '100%', order: 99, padding: 8, border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface-2)' }}>
      <select className="input sm" style={{ minWidth: 180 }} value={assignee} onChange={(e) => setAssignee(e.target.value)} autoFocus>
        <option value="">{t('task.pickAssignee')}</option>
        {departments.length > 0 && <optgroup label={t('chat.toDoDepts')}>{departments.map((d) => <option key={`d${d.id}`} value={`dept:${d.id}`}>{d.name}</option>)}</optgroup>}
        {teams.length > 0 && <optgroup label={t('chat.toDoTeams')}>{teams.map((tm) => <option key={`t${tm.id}`} value={`team:${tm.id}`}>{tm.name}</option>)}</optgroup>}
        {users.length > 0 && <optgroup label={t('task.people')}>{users.map((u) => <option key={`u${u.id}`} value={`user:${u.id}`}>{u.name}</option>)}</optgroup>}
      </select>
      <input className="input sm" style={{ flex: 1, minWidth: 220 }} placeholder={t('task.whatToDo')} value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void asignar(); if (e.key === 'Escape') setOpen(false); }} />
      <input className="input sm" type="date" value={due} onChange={(e) => setDue(e.target.value)} title={t('task.due')} />
      <button className="btn sm" disabled={busy || !assignee || !title.trim()} onClick={asignar}>{t('task.assign')}</button>
      <button className="btn ghost sm" onClick={() => setOpen(false)}><X size={13} strokeWidth={1.75} /></button>
    </div>
  );
}

export function ClaimChat({ claimId, channel, title, defaultChannel, summarySignal, noFollowUp, refreshKey }: {
  claimId: string; channel: string | null; title?: string; defaultChannel?: string | null; summarySignal?: number;
  /** Cambia cuando el padre quiere que el hilo se vuelva a pedir (ClaimDetail pasa el claim: cada recarga del claim —p. ej. tras
   *  preguntarle a Ray desde la tarjeta, que publica una nota automática en el General— trae la nota sin recargar la página). */
  refreshKey?: unknown;
  /** true = este chat NO maneja follow-ups (p. ej. Internal notes): sin header de fechas ni botón ⏰. */
  noFollowUp?: boolean;
}) {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { me } = useAuth();
  const myId = me?.kind === 'tenant' ? String(me.user.id) : null; // solo el autor edita su nota
  const myName = me?.kind === 'tenant' ? me.user.name : null; // avatar del que responde (caja de reply)
  const isDeptTab = !!channel;
  const [viewAll, setViewAll] = useState(!channel); // Overview arranca en General
  const [msgs, setMsgs] = useState<Msg[]>([]);
  /* TRADUCIR (Manuel 2026-10-08): por mensaje, "Translate" pide EN↔ES al servidor (se guarda allá) y
     la traducción se pinta debajo del texto; "Show original" la esconde sin perderla. */
  const [traducciones, setTraducciones] = useState<Record<string, { target_lang: 'en' | 'es'; html: string } | 'loading' | 'error'>>({});
  const [verOriginal, setVerOriginal] = useState<Set<string>>(new Set());
  async function traducir(m: Msg) {
    const guardada = m.translations_json ? Object.values(m.translations_json).find((x) => x?.html) : null;
    if (guardada) { setTraducciones((p) => ({ ...p, [m.id]: { target_lang: guardada.target_lang, html: guardada.html } })); return; }
    setTraducciones((p) => ({ ...p, [m.id]: 'loading' }));
    try {
      const r = await api.post<{ target_lang: 'en' | 'es'; html: string }>(`/api/claims/${claimId}/chat/${m.id}/translate`, { prefer: lang });
      setTraducciones((p) => ({ ...p, [m.id]: { target_lang: r.target_lang, html: r.html } }));
    } catch { setTraducciones((p) => ({ ...p, [m.id]: 'error' })); }
  }
  const [depts, setDepts] = useState<Dept[]>([]);
  // Email desplegado: contenido COMPLETO del email de un registro "📧 Email sent…" (por email_id).
  // Es lo que devuelve GET /api/claims/:id/emails/:emailId (getClaimEmail) y calza con el
  // EmailLike del hilo compartido: dirección, estado, quién lo mandó y el seguimiento
  // entregado → abierto → clic, que es lo que la tarjeta pinta en la cabecera.
  type EmailFull = {
    id: string; direction: string; recipient_type: string | null;
    from_address: string | null; to_address: string | null; cc_address: string | null;
    subject: string | null; body: string | null; status: string | null; created_at: string; author: string | null;
    delivered_at: string | null; opened_at: string | null; clicked_at: string | null;
    attachments?: Array<{ id: string; file_name: string | null; mime_type?: string | null }>;
  };
  const [emailView, setEmailView] = useState<Record<string, EmailFull | 'loading' | 'error'>>({});
  async function toggleEmail(msgId: string, emailId: string) {
    setEmailView((s) => {
      if (s[msgId] && s[msgId] !== 'error') { const n = { ...s }; delete n[msgId]; return n; } // colapsar
      return { ...s, [msgId]: 'loading' };
    });
    // Si ya estaba abierto lo colapsamos arriba; si no, cargamos.
    if (emailView[msgId] && emailView[msgId] !== 'error') return;
    try {
      const r = await api.get<{ email: EmailFull }>(`/api/claims/${claimId}/emails/${emailId}`);
      setEmailView((s) => ({ ...s, [msgId]: r.email }));
    } catch { setEmailView((s) => ({ ...s, [msgId]: 'error' })); }
  }
  // Editor rico (negritas/bullets/GIFs + @menciones): el contenido vive en el editor; aquí
  // solo se trackea si hay algo escrito (para habilitar Enviar).
  const richRef = useRef<RichEditorHandle>(null);
  const [hasBody, setHasBody] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  // "👷 CO": copiar lo que mandes (Send o Follow up) también al board del Contractor.
  const [alsoCo, setAlsoCo] = useState(false);
  // "Añadir también al To Do board": 't<id>' = team, 'd<id>' = departamento, '' = no crear tarea.
  const [toDo, setToDo] = useState('');
  const [teams, setTeams] = useState<Array<{ id: string; name: string }>>([]);
  const [deptTargets, setDeptTargets] = useState<Array<{ id: string; name: string }>>([]);
  // Canal destino al postear desde el General (dropdown). En un depto: su canal. Si no hay canal
  // fijo, defaultea al depto DUEÑO del status del claim (Leads→intake, Waiting approval→claims…).
  const [target, setTarget] = useState<string>(channel ?? defaultChannel ?? '');
  const targetTouched = useRef(false); // no pisar la elección manual del usuario
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState('');
  // Adjuntos del REPLY (reporte de Supplements: no podían adjuntar nada en las respuestas).
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [replyBusy, setReplyBusy] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editId, setEditId] = useState<string | null>(null);
  // EDICIÓN con el MISMO editor rico del composer: antes era un textarea plano que mostraba
  // el HTML crudo (<div style=…>) cuando la nota tenía formato (ej. pegada de ChatGPT).
  const editRichRef = useRef<RichEditorHandle>(null);
  const [editHas, setEditHas] = useState(false);
  const [editFiles, setEditFiles] = useState<File[]>([]); // adjuntos NUEVOS al editar (drag&drop / 📎)
  const [editDrag, setEditDrag] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [fu, setFu] = useState<{ last_follow_up_at: string | null; next_follow_up_at: string | null } | null>(null);
  // Edición manual del próximo follow-up (manager+, solo con un depto concreto activo).
  // La fecha del próximo follow-up la mueve CUALQUIERA (feedback 2026-07-29); cada cambio queda
  // en el activity log con la fecha anterior y la nueva.
  // …PERO solo si el depto lo permite (Manuel 2026-09-30: "no se cambia manual"; Settings → Departments,
  // apagado por defecto). Sin la bandera el lápiz no aparece y el endpoint contesta 403 igual.
  const canEditFu = hasRole(me, 'office') && Boolean(channel && !viewAll) && depts.find((d) => d.key === channel)?.manual_follow_up_date === true;
  const [fuEditing, setFuEditing] = useState(false);
  const [fuDraft, setFuDraft] = useState('');
  const [fuBusy, setFuBusy] = useState(false);
  async function saveNextFu(date: string | null) {
    if (!channel) return;
    setFuBusy(true);
    try {
      const r = await api.post<{ next_follow_up_at: string | null }>(`/api/claims/${claimId}/followup-next`, { department: channel, date });
      setFu((prev) => ({ last_follow_up_at: prev?.last_follow_up_at ?? null, next_follow_up_at: r.next_follow_up_at }));
      setFuEditing(false);
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setFuBusy(false); }
  }
  // Deep-link #msg-<id> (desde el DM/notificación de una mención): salta al update exacto y lo resalta.
  const [highlightId, setHighlightId] = useState<string | null>(() => /^#msg-(\w+)$/.exec(window.location.hash)?.[1] ?? null);
  // RECOGIDO POR DEFECTO (Manuel 2026-09-30): "los updates de supplements también los quiero recogidos
  // mostrando el último cuando está recogido, pero cuando lo abro se despliegan todas. Lo mismo para
  // Overview, Claims, Scheduling, Payments, Contractor y donde tenga updates en todo el app". Este
  // componente es el chat de TODOS esos lugares, así que la regla vive acá una sola vez. Se abre solo
  // si se entró por deep-link a un update (#msg-id) o si se está buscando.
  const [recogido, setRecogido] = useState<boolean>(() => !/^#msg-(\w+)$/.test(window.location.hash));
  // Si llega un deep-link estando ya montado (clic en una notificación), se despliega para que se vea.
  useEffect(() => { if (highlightId) setRecogido(false); }, [highlightId]);

  function cancelEdit() { setEditId(null); setEditHas(false); setEditFiles([]); setEditDrag(false); }
  async function saveEdit() {
    const html = (editRichRef.current?.getHtml() ?? '').trim();
    const text = (editRichRef.current?.getText() ?? '').trim();
    const has = !!text || html.includes('<img');
    if (!editId || (!has && editFiles.length === 0)) return;
    setEditBusy(true);
    try {
      // Adjuntos NUEVOS agregados al editar (imágenes/audio/…): se suben y se ANEXAN a los existentes.
      const attachments = await Promise.all(editFiles.map(async (f) => ({ key: await uploadFile(f, 'misc'), name: f.name })));
      await api.put(`/api/claims/${claimId}/chat/${editId}`, { body: has ? html : '', attachments });
      // Guardado → el borrador de la edición ya no aplica (clear() también lo borra del storage).
      editRichRef.current?.clear();
      cancelEdit(); load();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setEditBusy(false); }
  }

  const effChannel = channel && !viewAll ? channel : 'all';
  const deptName = (k: string | null) => (k
    ? (k === 'contractor' ? t('cd.tabContractor') : depts.find((d) => d.key === k)?.name ?? k)
    : null);

  const [kudoed, setKudoed] = useState<Set<string>>(new Set());
  // "Visto por" (👁): popover con quién vio el update + desde qué device.
  type Viewer = { user_id: string; name: string | null; device: string | null; viewed_at: string };
  const [viewersOpen, setViewersOpen] = useState<string | null>(null);
  const [viewers, setViewers] = useState<Record<string, Viewer[] | 'loading'>>({});
  async function toggleViewers(msgId: string) {
    if (viewersOpen === msgId) { setViewersOpen(null); return; }
    setViewersOpen(msgId);
    if (!viewers[msgId]) {
      setViewers((s) => ({ ...s, [msgId]: 'loading' }));
      try { const r = await api.get<{ viewers: Viewer[] }>(`/api/claims/${claimId}/chat/${msgId}/views`); setViewers((s) => ({ ...s, [msgId]: r.viewers })); }
      catch { setViewers((s) => ({ ...s, [msgId]: [] })); }
    }
  }
  const deviceIcon = (d: string | null) => d === 'Mobile'
    ? <Smartphone size={15} strokeWidth={1.75} />
    : d === 'Tablet' ? <Tablet size={15} strokeWidth={1.75} /> : <Monitor size={15} strokeWidth={1.75} />;
  async function load() {
    try {
      const ms = (await api.get<{ messages: Msg[] }>(`/api/claims/${claimId}/chat?channel=${effChannel}`)).messages;
      setMsgs(ms);
      // Kudos ya dados por mí sobre estos updates (para pintar el 👍 activo).
      const ids = ms.filter((m) => m.author_user_id).map((m) => m.id);
      if (ids.length) api.get<{ ids: string[] }>(`/api/evaluation/kudos/mine?entityType=claim_update&ids=${ids.join(',')}`).then((r) => setKudoed(new Set(r.ids))).catch(() => {});
    } catch { setMsgs([]); avisarFalloDeCarga('chat', () => load()); }
  }
  useEffect(() => {
    api.get<{ departments: Dept[] }>('/api/departments').then((r) => setDepts(r.departments)).catch(() => {});
    // Destinos del To Do: los MISMOS teams/deptos que se pueden mencionar (una sola fuente de
    // verdad para "a quién le puedo asignar trabajo").
    api.get<{ teams: Array<{ id: string; name: string }>; departments?: Array<{ id: string; name: string }> }>('/api/team/mention-targets')
      .then((r) => { setTeams(r.teams || []); setDeptTargets(r.departments || []); }).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [claimId, effChannel, refreshKey]);
  // Último / próximo follow-up del claim (header del módulo de updates). En un tab de depto se
  // pasa el depto → un UPDATE del depto cuenta como follow-up y resetea la alerta. Se re-consulta
  // cuando cambia la cantidad de mensajes (al postear un update → se actualiza al instante).
  useEffect(() => {
    if (noFollowUp) { setFu(null); return; }
    const qs = channel && !viewAll ? `?department=${encodeURIComponent(channel)}` : '';
    api.get<{ last_follow_up_at: string | null; next_follow_up_at: string | null }>(`/api/claims/${claimId}/followup-summary${qs}`)
      .then(setFu).catch(() => setFu(null));
  }, [claimId, channel, viewAll, msgs.length, summarySignal, noFollowUp]);
  // Newest-first (como Monday): lo más reciente arriba — ya no se auto-scrollea al fondo.
  // Solo el deep-link #msg-<id> sigue haciendo scroll a su update exacto.
  // El summary del drawer llega async → cuando aparece el depto dueño del status, defaultea el
  // canal a ese (salvo que el usuario ya haya elegido otro a mano).
  useEffect(() => {
    if (channel === null && defaultChannel && !targetTouched.current) setTarget(defaultChannel);
  }, [defaultChannel, channel]);
  // Contratista del claim → habilita la mención @contractor (inserta el nombre de la compañía).
  const [contractorName, setContractorName] = useState<string | null>(null);
  useEffect(() => {
    api.get<{ contractor_name?: string | null }>(`/api/claims/${claimId}`).then((r) => setContractorName(r.contractor_name ?? null)).catch(() => {});
  }, [claimId]);
  // Archivos del claim → para hacer HIPERLINK el nombre del archivo mencionado en las notas de
  // automation (ej. "File uploaded by the contractor: 'tarp-invoice.pdf'") y poder abrirlo ahí.
  const [claimFiles, setClaimFiles] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    api.get<{ files: Array<{ id: string; file_name: string | null }> }>(`/api/claims/${claimId}/files`)
      .then((r) => setClaimFiles((r.files || []).filter((f) => f.file_name).map((f) => ({ id: f.id, name: f.file_name! }))))
      .catch(() => {});
  }, [claimId]);
  // Envuelve el nombre de archivo (que la nota trae ENTRECOMILLADO) en un link a /inline. Solo se
  // usa en notas de automation (texto plano) → sin riesgo de romper HTML de mensajes ricos.
  const linkifyFiles = (text: string): string => {
    if (!claimFiles.length) return text;
    let out = text;
    for (const f of claimFiles) {
      const esc = f.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      out = out.replace(new RegExp(`"${esc}"`, 'g'), `"<a href="/api/files/${f.id}/inline" target="_blank" rel="noopener noreferrer" style="color:var(--info);font-weight:var(--fw-semibold)">${f.name}</a>"`);
    }
    return out;
  };
  // Cuando llegan los mensajes y hay un target del deep-link, hace scroll y lo resalta ~3s.
  useEffect(() => {
    if (!highlightId || !msgs.length) return;
    const el = document.getElementById(`msg-${highlightId}`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // 6.5s: el highlight debe SEGUIR visible cuando el layout termina de asentarse
    // (el re-scroll anti-shift vive en ClaimDetail y reintenta ~5s).
    const h = setTimeout(() => setHighlightId(null), 6500);
    return () => clearTimeout(h);
  }, [highlightId, msgs]);

  /**
   * CANDADO SINCRÓNICO contra el doble envío.
   *
   * `busy` es estado de React: se aplica en el próximo render, así que NO frena una ráfaga dentro
   * del mismo tick — y solo desactivaba los BOTONES, mientras que la tecla Enter llama a send()
   * directo. El 2026-08-06 eso publicó la misma nota 22 veces en 2 segundos (1 + 19 + 2, que es
   * exactamente la velocidad de auto-repetición del teclado): alguien dejó el Enter apretado. Cada
   * copia además se espejó a Monday, y de ahí volvieron como 22 updates que ya no se pueden borrar
   * desde acá. Un ref se ve en el mismo tick y corta la ráfaga en seco.
   */
  const enviando = useRef(false);
  const respondiendo = useRef(false);

  async function send(followUp = false) {
    if (enviando.current) return;
    // El body va en HTML (el editor rico); el texto plano solo valida que haya contenido.
    const html = (richRef.current?.getHtml() ?? '').trim();
    const text = (richRef.current?.getText() ?? '').trim();
    const hasContent = !!text || html.includes('<img');
    if (!hasContent && files.length === 0) return;
    enviando.current = true;
    setBusy(true);
    try {
      // Varios archivos en el mismo update (antes uno reemplazaba al otro).
      const attachments = await Promise.all(files.map(async (f) => ({ key: await uploadFile(f, 'misc'), name: f.name })));
      // En depto: postea a su canal. En General: al canal elegido (o general si vacío).
      const dept = channel ?? (target || null);
      // FOLLOW-UP EXPLÍCITO: solo el botón "⏰ Follow up" re-arma el reloj del depto.
      const posted = await api.post<{ id: string }>(`/api/claims/${claimId}/chat`, { department: dept, body: hasContent ? html : '', attachments, followUp });
      // Check "👷 CO": usa el MISMO forward que el botón por-mensaje → copia al board del
      // Contractor con la misma fecha/hora y deja el original marcado "Sent to CO ✓".
      if (alsoCo && dept !== 'contractor') {
        await api.post(`/api/claims/${claimId}/chat/${posted.id}/send-to-contractor`, {});
      }
      // La nota, además, como TAREA del team/depto elegido. Va DESPUÉS del post y en su propio
      // try: si la tarea falla, la nota ya quedó publicada — perderla sería mucho peor.
      if (toDo) {
        const esTeam = toDo.startsWith('t');
        const id = toDo.slice(1);
        // El título es el texto plano recortado (el HTML del editor no se lee en el To Do);
        // la nota completa va en el cuerpo, así no se pierde nada de lo que escribieron.
        const titulo = text.replace(/\s+/g, ' ').trim().slice(0, 120) || t('chat.toDoFallbackTitle');
        try {
          await api.post('/api/tasks', {
            claimId,
            title: titulo,
            note: text || null,
            assignedToTeamId: esTeam ? id : null,
            assignedToDepartmentId: esTeam ? null : id,
            // Con el origen, la notificación y el link de la tarea caen en ESTE mensaje (tab +
            // ancla) en vez del claim pelado: se lee la nota en su hilo, con lo que vino antes.
            sourceMessageId: posted.id,
            sourceDepartment: dept,
          });
        } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
      }
      richRef.current?.clear(); setFiles([]); setAlsoCo(false); setToDo('');
      await load();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { enviando.current = false; setBusy(false); }
  }

  /** Forward al board del Contractor de un update YA publicado (misma fecha/hora, mismo autor). */
  async function forwardToCo(id: string) {
    if (!window.confirm(t('chat.coForwardConfirm'))) return;
    try { await api.post(`/api/claims/${claimId}/chat/${id}/send-to-contractor`, {}); await load(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }

  /** Borra el mensaje; si viajó al contractor, JobFlow lo borra también (update.deleted). */
  async function deleteMsg(id: string) {
    if (!window.confirm(t('chat.deleteConfirm'))) return;
    try { await api.del(`/api/claims/${claimId}/chat/${id}`); await load(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }

  // Like (reacción ligera, distinta de los kudos de EOM): optimista + reload.
  async function like(id: string) {
    if (id.startsWith('b')) return; // los updates de Monday no se pueden likear
    setMsgs((ms) => ms.map((m) => (m.id === id
      ? { ...m, liked_by_me: !m.liked_by_me, like_count: m.like_count + (m.liked_by_me ? -1 : 1),
          liked_by: m.liked_by_me ? (m.liked_by ?? []).filter((n) => n !== (me?.kind === 'tenant' ? me.user.name : '')) : [...(m.liked_by ?? []), ...(me?.kind === 'tenant' && me.user.name ? [me.user.name] : [])] }
      : m)));
    try { await api.post(`/api/claims/${claimId}/chat/${id}/like`, {}); } catch { load(); }
  }
  async function sendReply(parentId: string) {
    if (respondiendo.current) return;
    if (!replyBody.trim() && replyFiles.length === 0) return;
    respondiendo.current = true;
    setReplyBusy(true);
    try {
      // Los adjuntos del reply van por la misma vía que los del update (uploadFile → attachments).
      const attachments = await Promise.all(replyFiles.map(async (f) => ({ key: await uploadFile(f, 'misc'), name: f.name })));
      // Los mensajes del contratista viven en board_updates (id "b123") y no pueden ser parent de
      // un claim_message. Ese reply sale como mensaje normal del canal — que es justo lo que llega
      // a JobFlow y marca el mensaje como respondido — con la cita ya puesta en el cuerpo.
      const pid = /^\d+$/.test(parentId) ? parentId : null;
      await api.post(`/api/claims/${claimId}/chat`, { department: channel ?? null, body: replyBody.trim(), parentId: pid, attachments });
      setReplyBody(''); setReplyFiles([]); setReplyTo(null); await load();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { respondiendo.current = false; setReplyBusy(false); }
  }

  const TRUNC = 320;
  /** Una tarjeta de update estilo Monday (avatar, autor, fecha, cuerpo con "See more", acciones). */
  function renderMsg(m: Msg, isReply: boolean) {
    // Record: el update lleva "[Watch](/rec/<id>)" al final para quien lo lee sin tarjeta (campana,
    // DM del inspector). Acá la RecordingCard ES el Watch: se quita esa línea del cuerpo pintado.
    const body = (m.recording_id ?? m.recordingId) != null ? (m.body || '').replace(/\s*\[Watch\]\(\/rec\/\d+\)\s*$/, '') : (m.body || '');
    const plain = body.replace(/<[^>]*>/g, '');
    const long = plain.length > TRUNC;
    const open = expanded.has(m.id);
    const hot = highlightId === m.id;
    return (
      <div key={m.id} id={`msg-${m.id}`} style={{
        padding: isReply ? '10px 14px' : '14px 16px',
        marginLeft: isReply ? 40 : 0,
        marginBottom: 8,
        borderRadius: 12,
        // La barra de color a la izquierda es lo que hace que el update "salte" en la lista.
        borderLeft: `4px solid ${COLOR_UPDATE(m.source, m.department)}`,
        background: isReply ? 'var(--card)' : 'var(--surface-2)',
        border: isReply ? '1px solid var(--line)' : undefined,
        ...(hot ? { boxShadow: '0 0 0 2px var(--accent)', background: 'var(--accent-soft)', transition: 'background .3s, box-shadow .3s' } : {}) }}>
        {/* Header del autor: en notas largas queda PEGADO arriba (sticky) mientras se scrollea el
            cuerpo — igual que Monday (la foto + nombre del que puso la nota se quedan estáticos). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          ...(isReply ? {} : { position: 'sticky', top: 0, zIndex: 2, background: hot ? 'var(--accent-soft)' : 'var(--surface-2)', padding: '4px 0', margin: '-4px 0 4px' }) }}>
          <Avatar userId={m.author_user_id} name={m.author} size={isReply ? 30 : 40} />
          {/* El nombre de quien dejó el update, con peso de verdad: es lo primero que se busca. */}
          <span style={{ fontWeight: 'var(--fw-bold)', fontSize: isReply ? 'var(--fs-base)' : 'var(--fs-md)', letterSpacing: -0.2, color: 'var(--ink)' }}>
            {m.author || (m.source === 'monday' ? 'CRM Automation' : '—')}
          </span>
          {!isReply && m.department && <span className="badge teal" style={{ fontSize: 'var(--fs-2xs)' }}>{deptName(m.department)}</span>}
          {/* Chip: este update contó como FOLLOW-UP (re-armó el reloj del depto). */}
          {m.is_follow_up && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--accent-soft)', color: 'var(--accent)', fontWeight: 'var(--fw-bold)' }} title={t('chat.followUpChipHint')}><AlarmClock size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('chat.followUpChip')}</span>}
          {m.source === 'monday' && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--accent-soft)', color: 'var(--accent)', fontWeight: 'var(--fw-bold)' }}><Settings size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> CRM Automation</span>}
          {/* Copias idénticas seguidas que llegaron de Monday, colapsadas en una sola tarjeta. */}
          {(m as Msg & { copias?: number }).copias! > 1 && (
            <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--red-soft)', color: 'var(--red)', fontWeight: 'var(--fw-bold)' }}
              title={t('chat.dupHint')}>×{(m as Msg & { copias?: number }).copias}</span>
          )}
          {/* Mensajes de la conversación con el CONTRACTOR (board compartido con JobFlow). */}
          {m.source === 'partner' && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--orange-soft)', color: 'var(--orange)', fontWeight: 'var(--fw-bold)' }}><HardHat size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('chat.contractorBoard')}</span>}
          {/* SIN RESPONDER: el aviso vive SOBRE el mensaje, no como una alerta aparte. Se calcula
              en la query, así que desaparece solo en cuanto alguien contesta — que es justo lo
              que la alerta materializada no hacía. */}
          {m.co_unanswered && (
            <span className="badge" title={t('chat.unansweredHint')}
              style={{ fontSize: 'var(--fs-2xs)', background: 'var(--red-soft)', color: 'var(--red)', fontWeight: 'var(--fw-bold)' }}>
              <AlarmClock size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('chat.unanswered')}{m.co_waiting_hours != null ? ` · ${m.co_waiting_hours}h` : ''}
            </span>
          )}
          <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{new Date(m.created_at).toLocaleString('en-US')}</span>
        </div>
        {editId === m.id ? (
          <div
            style={{ display: 'grid', gap: 6, marginTop: 4, position: 'relative', border: editDrag ? '2px dashed var(--accent)' : '2px dashed transparent', borderRadius: 10, padding: editDrag ? 6 : 0, background: editDrag ? 'var(--accent-soft)' : undefined }}
            onDragOver={(e) => { e.preventDefault(); setEditDrag(true); }}
            onDragLeave={() => setEditDrag(false)}
            onDrop={(e) => { e.preventDefault(); setEditDrag(false); const fs = Array.from(e.dataTransfer.files ?? []); if (fs.length) setEditFiles((prev) => [...prev, ...fs]); }}
          >
            <RichEditor key={m.id} ref={editRichRef} minHeight={40} mentions contractorName={contractorName} initialHtml={toEditorHtml(m.body || '')} onChange={setEditHas}
              draftKey={`chat-edit:${m.id}`}
              onPaste={(e) => { const fs = imagesFromClipboard(e); if (fs.length) { e.preventDefault(); setEditFiles((prev) => [...prev, ...fs]); } }} />
            {editFiles.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {editFiles.map((f, i) => (
                  <span key={i} className="muted" style={{ fontSize: 'var(--fs-2xs)', display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--surface-2)', borderRadius: 6, padding: '2px 6px' }}>
                    {/^audio\//.test(f.type) ? <Headphones size={12} strokeWidth={1.75} /> : /^image\//.test(f.type) ? <Image size={12} strokeWidth={1.75} /> : <Paperclip size={12} strokeWidth={1.75} />} {f.name}
                    <button className="btn ghost sm" style={{ padding: '0 4px' }} onClick={() => setEditFiles((prev) => prev.filter((_, j) => j !== i))}><X size={12} strokeWidth={1.75} /></button>
                  </span>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button className="btn sm" disabled={editBusy || (!editHas && editFiles.length === 0)} onClick={saveEdit}>{t('common.save')}</button>
              <button className="btn ghost sm" onClick={cancelEdit}>{t('common.cancel')}</button>
              <label className="btn ghost sm" style={{ cursor: 'pointer' }} title={t('upd.attach')}>
                <Paperclip size={14} strokeWidth={1.75} /><input type="file" multiple accept="image/*,audio/*,application/pdf" style={{ display: 'none' }} onChange={(e) => { const fs = Array.from(e.target.files ?? []); if (fs.length) setEditFiles((prev) => [...prev, ...fs]); e.target.value = ''; }} />
              </label>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('upd.dropHere') || 'Drop image/audio'}</span>
            </div>
          </div>
        ) : body && (
          <div style={{ fontSize: 'var(--fs-base)', lineHeight: 1.6, whiteSpace: 'pre-wrap', marginTop: 4 }}>
            <span dangerouslySetInnerHTML={{ __html: sanitize(linkifyUrls(m.source === 'monday' || m.source === 'partner' ? linkifyFiles(long && !open ? plain.slice(0, TRUNC) + '…' : body) : (long && !open ? plain.slice(0, TRUNC) + '…' : body))) }} />
            {long && <button className="btn ghost sm" style={{ padding: '1px 6px', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' }} onClick={() => setExpanded((s) => { const n = new Set(s); n.has(m.id) ? n.delete(m.id) : n.add(m.id); return n; })}>{open ? t('chat.seeLess') : t('chat.seeMore')}</button>}
          </div>
        )}
        {(() => {
          const tr = traducciones[m.id];
          if (!tr || tr === 'loading' || tr === 'error' || verOriginal.has(m.id)) return null;
          return (
            <div style={{ marginTop: 6, fontSize: 'var(--fs-base)', lineHeight: 1.6, whiteSpace: 'pre-wrap', background: 'var(--accent-soft)', borderLeft: '3px solid var(--accent)', borderRadius: 8, padding: '7px 10px' }}>
              <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>
                <Languages size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('chat.translation')} ({tr.target_lang.toUpperCase()})
              </div>
              <span dangerouslySetInnerHTML={{ __html: sanitize(linkifyUrls(tr.html)) }} />
            </div>
          );
        })()}
        {/* Grabación adjunta al claim (Record, spec §4.12.5): poster + Play inline; el worker deja
            además la fila en Files. */}
        {(m.recording_id ?? m.recordingId) != null && <div style={{ marginTop: 8 }}><RecordingCard id={Number(m.recording_id ?? m.recordingId)} /></div>}
        {/* Registro "📧 Email sent…": desplegar para ver el CONTENIDO COMPLETO del email. */}
        {m.email_id && (() => {
          const ev = emailView[m.id];
          const shown = ev && ev !== 'loading' && ev !== 'error';
          return (
            <div style={{ marginTop: 4 }}>
              <button className="btn ghost sm" style={{ padding: '2px 6px', fontSize: 'var(--fs-2xs)' }} onClick={() => toggleEmail(m.id, m.email_id!)}>
                {shown ? '▾' : '▸'} <Mail size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {shown ? t('chat.hideEmail') : t('chat.showEmail')}
              </button>
              {ev === 'loading' && <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 4 }}>…</div>}
              {ev === 'error' && <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 4 }}>{t('chat.emailErr')}</div>}
              {/* La MISMA tarjeta del hilo de correos (EmailCard): fondo por dirección, quién → a
                  quién, seguimiento entregado/abierto/clic y adjuntos. Antes era una caja gris
                  propia de este chat — la quinta versión distinta de la misma lista. */}
              {shown && (
                <div style={{ marginTop: 6 }}>
                  <EmailCard message={ev as EmailFull} defaultOpen showSubject onReply={(m) => navigate(rutaParaResponder(claimId, m))} />
                </div>
              )}
            </div>
          );
        })()}
        {(m.attachments && m.attachments.length ? m.attachments : (m.attachment_name ? [{ key: '', name: m.attachment_name }] : [])).length > 0 && (() => {
          const atts = m.attachments && m.attachments.length ? m.attachments : (m.attachment_name ? [{ key: '', name: m.attachment_name }] : []);
          const urlOf = (i: number) => `/api/claims/${claimId}/chat/${m.id}/attachment?i=${i}`;
          const isImg = (n: string) => /\.(png|jpe?g|gif|webp|bmp|heic)$/i.test(n);
          const isVideo = (n: string) => /\.(mp4|mov|m4v|webm|ogv|avi|mkv|3gp)$/i.test(n);
          const isAudio = (n: string) => /\.(mp3|wav|m4a|aac|ogg|oga|weba|amr)$/i.test(n);
          // Ícono + color por tipo de archivo (para las file cards grandes tipo Monday).
          const kindOf = (n: string) => {
            const e = (n.split('.').pop() || '').toLowerCase();
            if (e === 'pdf') return { Ic: FileText, c: '#dc2626' };
            if (['doc', 'docx'].includes(e)) return { Ic: FileText, c: '#2563eb' };
            if (['xls', 'xlsx', 'csv'].includes(e)) return { Ic: FileSpreadsheet, c: '#16a34a' };
            if (['zip', 'rar', '7z'].includes(e)) return { Ic: FileArchive, c: '#7c3aed' };
            if (['esx'].includes(e)) return { Ic: Puzzle, c: '#7c3aed' };
            return { Ic: FileText, c: 'var(--muted)' };
          };
          const docs = atts.map((a, i) => ({ a, i })).filter(({ a }) => !isImg(a.name) && !isVideo(a.name) && !isAudio(a.name));
          // "Download all files" (como Monday): dispara la descarga de cada archivo. Sin endpoint de
          // zip — usa un click por archivo (con ?download=1 el server manda Content-Disposition).
          const downloadAll = () => { atts.forEach((_a, i) => { const el = document.createElement('a'); el.href = `${urlOf(i)}&download=1`; el.rel = 'noreferrer'; document.body.appendChild(el); el.click(); el.remove(); }); };
          return (
            <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
              {atts.length > 1 && (
                <button className="btn ghost sm" style={{ justifySelf: 'start', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={downloadAll}>
                  <ArrowDown size={13} strokeWidth={1.75} /> {t('chat.downloadAll')} ({atts.length})
                </button>
              )}
              {/* Imágenes y videos GRANDES (preview visual, como Monday). */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {atts.map((_a, i) => {
                  const a = atts[i]!; const u = urlOf(i);
                  if (isImg(a.name)) return <a key={i} href={u} target="_blank" rel="noreferrer" title={a.name}><img src={u} alt={a.name} style={{ maxWidth: 440, maxHeight: 340, width: '100%', borderRadius: 12, border: '1px solid var(--line)', display: 'block', objectFit: 'cover' }} /></a>;
                  if (isVideo(a.name)) return <video key={i} controls preload="metadata" src={u} style={{ maxWidth: 480, width: '100%', borderRadius: 12, border: '1px solid var(--line)', background: '#000', display: 'block' }} />;
                  if (isAudio(a.name)) return <span key={i} style={{ display: 'inline-flex', flexDirection: 'column', gap: 3, minWidth: 240 }}><span className="muted" style={{ fontSize: 'var(--fs-xs)' }}><Headphones size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {a.name}</span><audio controls preload="none" src={u} style={{ height: 36, width: 260 }} /></span>;
                  return null;
                })}
              </div>
              {/* File cards GRANDES para documentos (PDF/DOC/XLS/ZIP…) — miniatura + nombre + descargar. */}
              {docs.length > 0 && (
                <div style={{ display: 'grid', gap: 8 }}>
                  {docs.map(({ a, i }) => {
                    const k = kindOf(a.name);
                    return (
                      <a key={i} href={urlOf(i)} target="_blank" rel="noreferrer" title={a.name}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid var(--line)', borderRadius: 12, padding: '12px 14px', background: 'var(--card)', textDecoration: 'none', color: 'var(--ink)', boxShadow: 'var(--shadow)', maxWidth: 480 }}>
                        <span style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--surface-2)', display: 'grid', placeItems: 'center', flexShrink: 0, border: `1px solid ${k.c}22` }}><k.Ic size={22} strokeWidth={1.75} color={k.c} /></span>
                        <span style={{ display: 'grid', gap: 2, minWidth: 0, flex: 1 }}>
                          <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{(a.name.split('.').pop() || '').toUpperCase()} · {t('chat.clickToOpen')}</span>
                        </span>
                        <span style={{ opacity: 0.5, flexShrink: 0, display: 'inline-flex' }}><ArrowDown size={16} strokeWidth={1.75} /></span>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
        {/* Acciones estilo Monday: Like (reacción) · Reply · Task · Kudos (EOM). `wrap`: el formulario
            de Task ocupa una línea entera debajo de los botones en vez de apretarlos. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
          {m.source !== 'monday' && m.source !== 'partner' && (
            <button className="btn ghost sm" style={{ padding: '3px 8px', fontSize: 'var(--fs-sm)', display: 'inline-flex', alignItems: 'center', gap: 5, color: m.liked_by_me ? 'var(--accent)' : undefined, fontWeight: m.liked_by_me ? 'var(--fw-bold)' : 'var(--fw-medium)' }} onClick={() => like(m.id)}>
              <ThumbsUp size={15} strokeWidth={2} fill={m.liked_by_me ? 'currentColor' : 'none'} /> {t('chat.like')}{m.like_count > 0 ? ` ${m.like_count}` : ''}
            </button>
          )}
          {/* QUIÉN dio like (Paula 2026-09-24): el número solo no dice nada. */}
          {m.source !== 'monday' && m.source !== 'partner' && (m.liked_by?.length ?? 0) > 0 && (
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }} title={m.liked_by!.join(', ')}>
              {m.liked_by!.length <= 3 ? m.liked_by!.join(', ') : `${m.liked_by!.slice(0, 3).join(', ')} +${m.liked_by!.length - 3}`}
            </span>
          )}
          {m.source !== 'monday' && (
            /* Reply también EN un reply: el hilo sigue bajo el mismo update padre (no se rompe
               la cadena) y se prellena @autor para que quede claro a quién le respondes.
               Al contratista (source partner) TAMBIÉN se le puede responder — pedido de los users
               2026-08-03: su nota salía sin botón. Ahí no hay hilo anidado posible, así que se
               prellena la cita de lo que escribió. */
            <button className="btn ghost sm" style={{ padding: '2px 6px', fontSize: 'var(--fs-2xs)' }} onClick={() => {
              const pid = isReply ? (m.parent_id ?? m.id) : m.id;
              const closing = replyTo === pid && !isReply;
              setReplyTo(closing ? null : pid);
              if (m.source === 'partner') {
                const cita = plain.replace(/\s+/g, ' ').trim().slice(0, 180);
                setReplyBody(cita ? `> ${cita}\n\n` : '');
              } else {
                setReplyBody(isReply && m.author ? `@${m.author} ` : '');
              }
              setReplyFiles([]);
            }}>↩ {t('chat.reply')}</button>
          )}
          {/* TRADUCIR EN↔ES (Manuel 2026-10-08): la traducción sale debajo del texto; el botón alterna con "Show original". */}
          {plain.trim() && (() => {
            const tr = traducciones[m.id];
            if (tr === 'loading') return <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}><Languages size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('chat.translating')}</span>;
            if (tr && tr !== 'error') {
              const oculta = verOriginal.has(m.id);
              return <button className="btn ghost sm" style={{ padding: '2px 6px', fontSize: 'var(--fs-2xs)' }} onClick={() => setVerOriginal((p) => { const n = new Set(p); n.has(m.id) ? n.delete(m.id) : n.add(m.id); return n; })}><Languages size={12} strokeWidth={1.75} /> {oculta ? t('chat.showTranslation') : t('chat.showOriginal')}</button>;
            }
            return <button className="btn ghost sm" style={{ padding: '2px 6px', fontSize: 'var(--fs-2xs)', ...(tr === 'error' ? { color: 'var(--red)' } : {}) }} title={t('chat.translateHint')} onClick={() => void traducir(m)}><Languages size={12} strokeWidth={1.75} /> {tr === 'error' ? t('chat.translateFailed') : t('chat.translate')}</button>;
          })()}
          {m.source !== 'monday' && (
            <TaskDesdeNota claimId={claimId} messageId={m.id} department={m.department ?? channel ?? null} plain={plain} />
          )}
          {m.source !== 'monday' && m.source !== 'partner' && editId !== m.id && myId && m.author_user_id === myId && (
            <button className="btn ghost sm" style={{ padding: '2px 6px', fontSize: 'var(--fs-2xs)' }} title={t('common.edit')} onClick={() => { setEditId(m.id); setEditHas(!!m.body); }}><Pencil size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('common.edit')}</button>
          )}
          {/* BORRAR (feedback 2026-07-29): autor o manager+. Nunca lo que escribió el
              contractor (source partner/monday). Si el mensaje viajó a JobFlow, allá también
              se borra (evento update.deleted). */}
          {m.source !== 'monday' && m.source !== 'partner'
            && ((myId && m.author_user_id === myId) || hasRole(me, 'manager')) && (
            <button
              className="btn ghost sm" style={{ padding: '2px 6px', fontSize: 'var(--fs-2xs)', color: 'var(--red)' }}
              title={t('chat.deleteHint')}
              onClick={() => void deleteMsg(m.id)}
            ><Trash2 size={14} strokeWidth={1.75} /></button>
          )}
          {m.author_user_id && <KudosButton entityType="claim_update" entityId={m.id} recipientUserId={m.author_user_id} given={kudoed.has(m.id)} label={t('chat.kudos')} entityPreview={{ text: plain.slice(0, 120), author: m.author, claim_id: claimId }} />}
          {/* Forward al Contractor: copia este update al board del CO con su fecha/hora original
              (para cuando se olvidó el check 👷 CO al postear). Ya enviado → estado, no botón. */}
          {m.source !== 'monday' && m.source !== 'partner' && m.department !== 'contractor' && (
            m.co_forwarded_at
              ? <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }} title={new Date(m.co_forwarded_at).toLocaleString('en-US')}><HardHat size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> <Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('chat.coForwarded')}</span>
              // Una grabación NUNCA sale al contratista (el backend contesta 400): el botón se apaga y lo dice.
              : <button className="btn ghost sm" style={{ padding: '2px 6px', fontSize: 'var(--fs-2xs)', color: 'var(--orange)' }} disabled={(m.recording_id ?? m.recordingId) != null} title={(m.recording_id ?? m.recordingId) != null ? t('rec.contractorNo') : t('chat.coForwardHint')} onClick={() => forwardToCo(m.id)}><HardHat size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('chat.coForward')}</button>
          )}
          {/* 👁 "Visto por": conteo + popover con quién lo vio y desde qué device (como Monday). */}
          {!isReply && m.source !== 'monday' && m.source !== 'partner' && (m.view_count ?? 0) > 0 && (
            <span style={{ position: 'relative', marginLeft: 'auto' }}>
              <button className="btn ghost sm" title={t('chat.seenBy')} style={{ padding: '3px 8px', fontSize: 'var(--fs-xs)', display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--muted)' }} onClick={() => toggleViewers(m.id)}>
                <Eye size={15} strokeWidth={2} /> {m.view_count}
              </button>
              {viewersOpen === m.id && (
                <>
                  <div onClick={() => setViewersOpen(null)} style={{ position: 'fixed', inset: 0, zIndex: 4000 }} />
                  {/* zIndex 4001: por encima de su backdrop (4000) — si no, el backdrop se come
                      los clicks y el scroll del popup (mismo bug del Attach del composer). */}
                  <div style={{ position: 'absolute', right: 0, bottom: '100%', marginBottom: 6, zIndex: 4001, minWidth: 230, maxHeight: 280, overflowY: 'auto', background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,.18)', padding: 6 }}>
                    <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4, padding: '4px 8px' }}>{t('chat.seenBy')}</div>
                    {viewers[m.id] === 'loading' ? <div className="muted" style={{ fontSize: 'var(--fs-2xs)', padding: 8 }}>…</div>
                      : ((viewers[m.id] as Viewer[] | undefined)?.length) ? (viewers[m.id] as Viewer[]).map((v) => (
                        <div key={v.user_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px' }}>
                          <Avatar userId={v.user_id} name={v.name} size={26} />
                          <span style={{ fontSize: 'var(--fs-sm)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.name || '—'}</span>
                          <span title={`${v.device || ''} · ${new Date(v.viewed_at).toLocaleString('en-US')}`} style={{ fontSize: 'var(--fs-base)' }}>{deviceIcon(v.device)}</span>
                        </div>
                      )) : <div className="muted" style={{ fontSize: 'var(--fs-2xs)', padding: 8 }}>—</div>}
                  </div>
                </>
              )}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      {/* Las clases clchat-* no traen estilo propio: son anclas para el modo móvil simple del
          drawer (.clchat-msimple esconde el chrome y deja SOLO los mensajes, como el mockup). */}
      <div className="clchat-head" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h2 className="card-title" style={{ margin: 0 }}>
          {isDeptTab ? (viewAll ? t('chat.general') : (title || t('chat.deptUpdates'))) : t('chat.general')}
        </h2>
        {/* Buscador de updates (finder) junto al título — con lupa. */}
        <div style={{ position: 'relative', flex: '1 1 160px', minWidth: 130, maxWidth: 280 }}>
          <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', display: 'flex', pointerEvents: 'none' }}><Search size={13} strokeWidth={1.75} /></span>
          <input className="input sm" style={{ width: '100%', paddingLeft: 28 }} placeholder={t('upd.search')} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <span style={{ flex: 1 }} />
        {isDeptTab && (
          <div style={{ display: 'flex', gap: 4 }}>
            <button className={`btn ghost sm${!viewAll ? ' active' : ''}`} onClick={() => setViewAll(false)}>{deptName(channel)}</button>
            <button className={`btn ghost sm${viewAll ? ' active' : ''}`} onClick={() => setViewAll(true)}>{t('chat.all')}</button>
          </div>
        )}
      </div>

      {/* Último / próximo follow-up del claim (rojo si el próximo ya venció). El MANAGER
          puede fijar/corregir la fecha del próximo a mano (✎) — re-arma el semáforo. */}
      {fu && (fu.last_follow_up_at || fu.next_follow_up_at || canEditFu) && (
        <div className="muted clchat-fu" style={{ fontSize: 'var(--fs-2xs)', marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span><CalendarDays size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('fu.lastFu')}: {fu.last_follow_up_at ? new Date(fu.last_follow_up_at).toLocaleDateString(localeUI()) : '—'}</span>
          {fuEditing ? (
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              {t('fu.nextFu')}:
              <input type="date" className="input sm" value={fuDraft} onChange={(e) => setFuDraft(e.target.value)} style={{ padding: '2px 6px' }} />
              <button className="btn sm" disabled={fuBusy || !fuDraft} onClick={() => saveNextFu(fuDraft)}>{t('fu.save')}</button>
              {fu.next_follow_up_at && <button className="btn ghost sm" disabled={fuBusy} onClick={() => saveNextFu(null)} title={t('fu.clearNext')}><Trash2 size={13} strokeWidth={1.75} /></button>}
              <button className="btn ghost sm" disabled={fuBusy} onClick={() => setFuEditing(false)}><X size={13} strokeWidth={1.75} /></button>
            </span>
          ) : (
            <span style={{ color: fu.next_follow_up_at && new Date(fu.next_follow_up_at).getTime() <= Date.now() ? 'var(--red)' : undefined }}>
              {t('fu.nextFu')}: {fu.next_follow_up_at ? new Date(fu.next_follow_up_at).toLocaleDateString(localeUI()) : '—'}
              {canEditFu && (
                <button
                  className="btn ghost sm" title={t('fu.editNext')} style={{ marginLeft: 4, padding: '0 6px' }}
                  onClick={() => { setFuDraft(fu.next_follow_up_at ? new Date(fu.next_follow_up_at).toISOString().slice(0, 10) : ''); setFuEditing(true); }}
                ><Pencil size={13} strokeWidth={1.75} /></button>
              )}
            </span>
          )}
        </div>
      )}

      {/* COMPOSER ARRIBA (como Monday): escribes arriba y lo nuevo aparece justo debajo. */}
      {/* Editor ARRIBA (full width). Debajo: selector de depto (izq) + botones. */}
      <div
        className="clchat-compose"
        style={{ marginTop: 8, position: 'relative', border: dragOver ? '2px dashed var(--accent)' : '2px dashed transparent', borderRadius: 10, padding: dragOver ? 6 : 0, background: dragOver ? 'var(--accent-soft)' : undefined, transition: 'background .1s' }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); const fs = Array.from(e.dataTransfer.files ?? []); if (fs.length) setFiles((prev) => [...prev, ...fs]); }}
      >
        {dragOver && <div className="muted" style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', fontSize: 'var(--fs-2xs)', pointerEvents: 'none', zIndex: 2 }}><ArrowDown size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('upd.dropHere') || 'Drop file'}</div>}
        {/* Campo donde se escribe el update (editor rico + @menciones + pegar imagen). */}
        <RichEditor ref={richRef} placeholder={t('chat.write')} minHeight={84} onEnter={() => send()} mentions contractorName={contractorName} onChange={setHasBody}
          draftKey={`chat:${claimId}:${channel ?? 'overview'}`}
          onPaste={(e) => { const fs = imagesFromClipboard(e); if (fs.length) { e.preventDefault(); setFiles((prev) => [...prev, ...fs]); } }} />
        {/* Controles ABAJO: depto destino a la IZQUIERDA + adjuntar + CO + Send + Follow up. */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
          {!isDeptTab && (
            <select className="input sm" style={{ width: 150 }} value={target} onChange={(e) => { targetTouched.current = true; setTarget(e.target.value); }} title={t('chat.postTo')}>
              <option value="">{t('chat.general')}</option>
              {depts.filter((d) => d.key !== 'payments').map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
              <option value="payments">Payments</option>
            </select>
          )}
          <label className="btn ghost sm" style={{ cursor: 'pointer' }} title={t('upd.attach')}>
            <Paperclip size={14} strokeWidth={1.75} /><input type="file" multiple style={{ display: 'none' }} onChange={(e) => { const fs = Array.from(e.target.files ?? []); if (fs.length) setFiles((prev) => [...prev, ...fs]); e.target.value = ''; }} />
          </label>
          {channel !== 'contractor' && (
            <label title={t('chat.sendToCoHint')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-2xs)', cursor: 'pointer', userSelect: 'none', color: alsoCo ? 'var(--orange)' : 'var(--muted)', fontWeight: alsoCo ? 'var(--fw-bold)' : 'var(--fw-body)' }}>
              <input type="checkbox" checked={alsoCo} onChange={(e) => setAlsoCo(e.target.checked)} style={{ accentColor: 'var(--orange)' }} />
              <HardHat size={13} strokeWidth={1.75} /> CO
            </label>
          )}
          {/* ══ LA NOTA TAMBIÉN COMO TAREA ══
              Una nota es información; una tarea es trabajo asignado. Hoy quien deja la nota
              tiene que ir aparte al To Do y volver a escribir lo mismo para que alguien la
              agarre — así que muchas veces no la crea y la nota queda ahí sin dueño. Este
              selector manda la MISMA nota al board del team o depto elegido, de una. */}
          <select className="input sm" style={{ width: 148 }} value={toDo} onChange={(e) => setToDo(e.target.value)} title={t('chat.toDoHint')}>
            <option value="">{t('chat.toDoNone')}</option>
            {teams.length > 0 && (
              <optgroup label={t('chat.toDoTeams')}>
                {teams.map((x) => <option key={`t${x.id}`} value={`t${x.id}`}>{x.name}</option>)}
              </optgroup>
            )}
            {deptTargets.length > 0 && (
              <optgroup label={t('chat.toDoDepts')}>
                {deptTargets.map((x) => <option key={`d${x.id}`} value={`d${x.id}`}>{x.name}</option>)}
              </optgroup>
            )}
          </select>
          {/* Send + Follow up SIEMPRE juntos y a la derecha (no se desfasa el Follow up al angostar). */}
          <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4, flexShrink: 0 }}>
            {/* Send = nota interna (NO corre el reloj). Enter hace esto. */}
            <button className="btn ghost sm" disabled={busy || (!hasBody && files.length === 0)} onClick={() => send()} title={t('chat.sendHint')}>{t('common.send')}</button>
            {/* ⏰ Follow up = postea Y re-arma el reloj del depto (botón prominente).
                Los chats sin follow-up (Internal notes) no lo muestran. */}
            {!noFollowUp && (
              <button className="btn primary sm" disabled={busy || (!hasBody && files.length === 0)} onClick={() => send(true)} title={t('chat.followUpHint')}><AlarmClock size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('chat.followUpBtn')}</button>
            )}
          </div>
        </div>
      </div>
      {!isDeptTab && target && <div className="muted clchat-compose-aux" style={{ fontSize: 'var(--fs-2xs)', marginTop: 4 }}>{t('chat.willPost')} <b>{deptName(target) || target}</b></div>}
      {files.length > 0 && (
        <div className="clchat-compose-aux" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
          {files.map((f, i) => (
            <span key={i} className="muted" style={{ fontSize: 'var(--fs-2xs)', display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--surface-2)', borderRadius: 6, padding: '2px 6px' }}>
              <Paperclip size={12} strokeWidth={1.75} /> {f.name} <button className="btn ghost sm" style={{ padding: '0 4px' }} onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}><X size={12} strokeWidth={1.75} /></button>
            </span>
          ))}
        </div>
      )}

      {(() => {
        const ql = q.trim().toLowerCase();
        const hasContent = (m: Msg) => !!(m.body && (m.body.replace(/<[^>]*>/g, '').trim() || m.body.includes('<img'))) || !!m.attachment_name;
        const match = (m: Msg) => !ql || `${(m.body || '').replace(/<[^>]*>/g, '')} ${m.author ?? ''}`.toLowerCase().includes(ql);
        const replies: Record<string, Msg[]> = {};
        for (const m of msgs) if (m.parent_id) (replies[m.parent_id] ??= []).push(m);
        // Solo updates top-level CON contenido (evita filas vacías que se ven como líneas).
        // MÁS RECIENTE PRIMERO (como Monday): ya no hay que scrollear hasta abajo.
        const topsCrudos = msgs.filter((m) => !m.parent_id && hasContent(m) && match(m)).reverse();
        /**
         * COLAPSA las copias idénticas que llegan de Monday.
         *
         * El 2026-08-06 un claim mostró la MISMA nota 22 veces seguidas. No es un bug de acá: eran
         * 22 updates distintos en Monday (22 external_update_id diferentes, ids consecutivos, todos
         * creados en el mismo instante) y el espejo los trajo tal cual. Mientras eso se arregla del
         * lado de Monday, el equipo no puede quedarse con un hilo ilegible.
         *
         * Solo se agrupan las CONSECUTIVAS con el mismo autor y el mismo texto: dos notas iguales
         * escritas en días distintos quedan separadas por otras y no se tocan. No se borra nada —
         * las copias siguen en la base y el contador dice cuántas son.
         */
        const tops: Array<Msg & { copias?: number }> = [];
        for (const m of topsCrudos) {
          const prev = tops[tops.length - 1];
          const igual = prev && prev.source === 'monday' && m.source === 'monday'
            && (prev.author ?? '') === (m.author ?? '') && (prev.body ?? '') === (m.body ?? '');
          if (igual) { prev.copias = (prev.copias ?? 1) + 1; continue; }
          tops.push({ ...m });
        }
        // Recogido: solo el ÚLTIMO update (la lista va del más reciente al más viejo). Buscando se ven todos.
        const plegable = !ql && tops.length > 1;
        const visibles = plegable && recogido ? tops.slice(0, 1) : tops;
        return (
      // Feed SIEMPRE full-height (como la ventana derecha / Monday): sin caja de 420px con scroll
      // interno — la página scrollea. Mismo look en el claim completo y en todos los dept updates.
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '12px 0' }}>
        {tops.length === 0 && <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{ql ? t('upd.noMatch') : t('chat.empty')}</div>}
        {visibles.map((m) => (
          <div key={m.id} style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 12, boxShadow: 'var(--shadow)', flexShrink: 0 }}>
            {renderMsg(m, false)}
            {(replies[m.id] || []).filter(hasContent).map((rp) => renderMsg(rp, true))}
            {/* Caja de REPLY SIEMPRE visible bajo cada update (como Monday): avatar del usuario +
                un campo "Write a reply…". Al hacer click se activa el editor completo. */}
            {replyTo !== m.id && m.source !== 'monday' && m.source !== 'partner' && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 18px 12px', borderTop: '1px solid var(--line)' }}>
                <Avatar userId={myId} name={myName} size={32} />
                <button
                  onClick={() => { setReplyTo(m.id); setReplyBody(''); setReplyFiles([]); }}
                  style={{ flex: 1, textAlign: 'left', border: '1px solid var(--line)', borderRadius: 22, background: 'var(--card)', padding: '10px 16px', color: 'var(--muted)', fontSize: 'var(--fs-sm)', cursor: 'text' }}
                >{t('chat.replyPlaceholder')}</button>
              </div>
            )}
            {replyTo === m.id && (
              /* Caja de REPLY estilo Monday: avatar del que responde a la IZQUIERDA + campo grande.
                 Con adjuntos: 📎 + pegar captura (Cmd+V) + arrastrar el archivo encima. */
              <div
                style={{ display: 'grid', gap: 8, padding: '12px 18px 14px', borderTop: '1px solid var(--line)', background: 'var(--surface-2)' }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); const fs = Array.from(e.dataTransfer.files ?? []); if (fs.length) setReplyFiles((prev) => [...prev, ...fs]); }}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <Avatar userId={myId} name={myName} size={32} />
                  <div style={{ flex: 1 }}>
                    <MentionTextarea value={replyBody} onChange={setReplyBody} placeholder={t('chat.replyPh')} minHeight={40} onEnter={() => sendReply(m.id)}
                      onPaste={(e) => { const fs = imagesFromClipboard(e); if (fs.length) { e.preventDefault(); setReplyFiles((prev) => [...prev, ...fs]); } }} />
                  </div>
                  <label className="btn ghost sm" style={{ cursor: 'pointer', alignSelf: 'flex-end' }} title={t('upd.attach')}>
                    <Paperclip size={14} strokeWidth={1.75} /><input type="file" multiple style={{ display: 'none' }} onChange={(e) => { const fs = Array.from(e.target.files ?? []); if (fs.length) setReplyFiles((prev) => [...prev, ...fs]); e.target.value = ''; }} />
                  </label>
                  <button className="btn sm" style={{ alignSelf: 'flex-end' }} disabled={replyBusy || (!replyBody.trim() && replyFiles.length === 0)} onClick={() => sendReply(m.id)}>{t('common.send')}</button>
                  <button className="btn ghost sm" style={{ alignSelf: 'flex-end' }} title={t('common.cancel')} onClick={() => { setReplyTo(null); setReplyBody(''); setReplyFiles([]); }}><X size={13} strokeWidth={1.75} /></button>
                </div>
                {replyFiles.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {replyFiles.map((f, i) => (
                      <span key={i} className="muted" style={{ fontSize: 'var(--fs-2xs)', display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--surface-2)', borderRadius: 6, padding: '2px 6px' }}>
                        {/^image\//.test(f.type) ? <Image size={12} strokeWidth={1.75} /> : <Paperclip size={12} strokeWidth={1.75} />} {f.name}
                        <button className="btn ghost sm" style={{ padding: '0 4px' }} onClick={() => setReplyFiles((prev) => prev.filter((_, j) => j !== i))}><X size={12} strokeWidth={1.75} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {plegable && (
          <button
            type="button" className="btn ghost sm" onClick={() => setRecogido(!recogido)}
            style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {recogido
              ? <><ChevronDown size={14} strokeWidth={1.75} /> {t('upd.showAll', { n: tops.length })}</>
              : <><ChevronUp size={14} strokeWidth={1.75} /> {t('upd.showLatest')}</>}
          </button>
        )}
      </div>
        );
      })()}
    </div>
  );
}

/** Prepara el body para EDITARLO en el editor rico: HTML → sanitizado tal cual; texto
 *  plano → escapado con <br> (para que los saltos de línea no se pierdan al editarlo). */
function toEditorHtml(bodyRaw: string): string {
  const body = quitarMarcasDeAcronimos(bodyRaw); // la marca de pantalla nunca entra al editor
  if (/<(br|b|strong|i|em|u|s|strike|a|p|div|span|ul|ol|li|img)\b[^>]*>/i.test(body)) return sanitize(body);
  return body.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
}

/** Sanitiza básico el HTML de Monday/editor rico (formato + GIFs), sin handlers ni javascript:. */
/**
 * UN LINK PEGADO EN UN MENSAJE SE ABRE Y SE COPIA — no se transcribe a mano.
 *
 * Pedido del dueño (2026-08-21): el contratista manda desde JobFlow "Photos with the repairs:
 * https://app.companycam.com/project_invitation/…" y eso llegaba como texto plano. Para verlo
 * había que seleccionarlo con el mouse sin pasarse de un carácter.
 *
 * SOLO TOCA EL TEXTO, NUNCA EL MARCADO. Se parte por etiquetas y se transforma únicamente lo que
 * hay ENTRE ellas; además se lleva la cuenta de si vamos dentro de un `<a>`, para no meter un
 * enlace adentro de otro. Sin eso, un `href="https://…"` sería reescrito y el HTML del editor
 * rico quedaría roto — que es peor que el problema que esto resuelve.
 */
const URL_SUELTA = /\bhttps?:\/\/[^\s<>"']+/g;

export function linkifyUrls(html: string): string {
  let dentroDeLink = false;
  return html.split(/(<[^>]*>)/g).map((trozo) => {
    if (trozo.startsWith('<')) {
      if (/^<a\b/i.test(trozo)) dentroDeLink = true;
      else if (/^<\/a\s*>/i.test(trozo)) dentroDeLink = false;
      return trozo;
    }
    if (dentroDeLink || !trozo) return trozo;
    return trozo.replace(URL_SUELTA, (u) => {
      // La puntuación final casi nunca es parte de la URL: "mirá https://x.com/a." o "(https://x)".
      const m = u.match(/[.,;:!?)\]]+$/);
      const cola = m ? m[0] : '';
      const limpia = cola ? u.slice(0, -cola.length) : u;
      return `<a href="${limpia}" target="_blank" rel="noopener noreferrer" style="color:var(--info);font-weight:var(--fw-semibold);word-break:break-all">${limpia}</a>${cola}`;
    });
  }).join('');
}
